import { useState, useEffect, useCallback, useRef } from 'react';
import { devLog } from "../utils/devLog";

/**
 * Custom hook for managing PagBrasil.JS Card SDK
 * 
 * Handles SDK initialization, card data collection, and tokenization.
 * PagBrasil.JS uses HTML inputs with PB_* attributes to securely collect card data.
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.token - PagBrasil merchant token (pbtoken)
 * @param {string} config.oauthToken - OAuth token for authorized transactions (optional)
 * @param {boolean} config.isSandbox - Whether to use sandbox/homolog environment
 * @param {Function} config.onReady - Callback when SDK is ready
 * @param {Function} config.onError - Callback when error occurs
 * @param {Function} config.onTokenSuccess - Callback when tokenization succeeds
 * 
 * @returns {Object} Hook state and methods
 */
export const usePagbrasilCard = ({
  token,
  oauthToken,
  isSandbox = false,
  onReady,
  onError,
  onTokenSuccess,
}) => {
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cardToken, setCardToken] = useState(null);

  // Refs for callbacks to avoid stale closures
  const onReadyRef = useRef(onReady);
  const onErrorRef = useRef(onError);
  const onTokenSuccessRef = useRef(onTokenSuccess);
  const initializedRef = useRef(false);

  // Update refs when callbacks change
  useEffect(() => {
    onReadyRef.current = onReady;
    onErrorRef.current = onError;
    onTokenSuccessRef.current = onTokenSuccess;
  }, [onReady, onError, onTokenSuccess]);

  /**
   * Check if PagBrasil.JS SDK is loaded
   */
  const isSDKLoaded = useCallback(() => {
    return typeof window.$PB !== 'undefined';
  }, []);


  /**
   * Set card data for payment/tokenization
   * 
   * @param {Object} cardData - Card information
   * @param {string} cardData.holder - Cardholder name
   * @param {string} cardData.number - Card number
   * @param {string} cardData.expiration - Expiration date (MM/YY)
   * @param {string} cardData.cvv - CVV/CVC code
   * @param {number} cardData.save - 0 or 1, whether to save card
   */
  const setCardData = useCallback((cardData) => {
    if (!isSDKLoaded() || !initializedRef.current) {
      console.warn('PagBrasil SDK not initialized');
      return;
    }

    try {
      window.$PB.setCard({
        cc_holder: cardData.holder,
        cc_number: cardData.number,
        cc_expiration: cardData.expiration,
        cc_cvv: cardData.cvv,
        cc_save: 1,
        cc_auth: 0, // Authorize and capture
      });
    } catch (err) {
      console.error('Failed to set card data:', err);
      setError(err.message);
    }
  }, [isSDKLoaded]);

  /**
   * Set customer data (required for Brazil)
   * 
   * @param {Object} customerData - Customer information
   */
  const setCustomerData = useCallback((customerData) => {
    if (!isSDKLoaded() || !initializedRef.current) {
      console.warn('PagBrasil SDK not initialized');
      return;
    }

    try {
      window.$PB.setCustomer({
        customer_name: customerData.name,
        customer_taxid: customerData.taxId, // CPF or CNPJ
        customer_email: customerData.email,
        customer_phone: customerData.phone,
        address_street: customerData.street,
        address_zip: customerData.zip,
        address_city: customerData.city,
        address_state: customerData.state, // 2-letter state code
      });
    } catch (err) {
      console.error('Failed to set customer data:', err);
      setError(err.message);
    }
  }, [isSDKLoaded]);

  /**
   * Set payment configuration
   * 
   * @param {Object} paymentConfig - Payment settings
   * @param {number} paymentConfig.installments - Number of installments (1-12)
   */
  const setPaymentConfig = useCallback((paymentConfig) => {
    if (!isSDKLoaded() || !initializedRef.current) {
      console.warn('PagBrasil SDK not initialized');
      return;
    }

    try {
      window.$PB.setPayment({
        payment_method: "C", // Credit card
        cc_installments: paymentConfig.installments || 1,
      });
    } catch (err) {
      console.error('Failed to set payment config:', err);
      setError(err.message);
    }
  }, [isSDKLoaded]);

  /**
   * Set order data
   * 
   * @param {Object} orderData - Order information
   */
  const setOrderData = useCallback((orderData) => {
    if (!isSDKLoaded() || !initializedRef.current) {
      console.warn('PagBrasil SDK not initialized');
      return;
    }

    try {
      window.$PB.setOrder({
        order: orderData.orderId,
        product_name: orderData.productName,
        amount_brl: orderData.amount,
        param_url: orderData.paramUrl || '',
      });
    } catch (err) {
      console.error('Failed to set order data:', err);
      setError(err.message);
    }
  }, [isSDKLoaded]);

  /**
   * Tokenize card data
   * Returns a token that can be used for future charges
   * 
   * @returns {Promise<string>} Card token
   */
  const tokenize = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!isSDKLoaded() || !initializedRef.current) {
        const err = new Error('PagBrasil SDK not initialized');
        reject(err);
        return;
      }

      setLoading(true);
      setError(null);

      const onSuccess = (result) => {
        devLog('Card tokenized successfully:', result);
        setLoading(false);
        setCardToken(result.token);
        onTokenSuccessRef.current?.(result);
        resolve(result.token);
      };

      const onFailure = (result) => {
        console.error('Card tokenization failed:', result);
        setLoading(false);
        const errorMsg = result.error || 'Tokenization failed';
        setError(errorMsg);
        onErrorRef.current?.(errorMsg);
        reject(new Error(errorMsg));
      };

      try {
        window.$PB.doAddToken(onSuccess, onFailure);
      } catch (err) {
        setLoading(false);
        const errorMsg = err.message || 'Tokenization failed';
        setError(errorMsg);
        onErrorRef.current?.(errorMsg);
        reject(err);
      }
    });
  }, [isSDKLoaded]);

  /**
   * Process full payment (alternative to tokenize)
   * Submits order directly to PagBrasil
   * 
   * @returns {Promise<Object>} Payment result
   */
  const processPayment = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!isSDKLoaded() || !initializedRef.current) {
        const err = new Error('PagBrasil SDK not initialized');
        reject(err);
        return;
      }

      setLoading(true);
      setError(null);

      const onSuccess = (result) => {
        devLog('Payment processed:', result);
        setLoading(false);
        resolve(result);
      };

      const onFailure = (result) => {
        console.error('Payment failed:', result);
        setLoading(false);
        const errorMsg = result.error || 'Payment failed';
        setError(errorMsg);
        onErrorRef.current?.(errorMsg);
        reject(new Error(errorMsg));
      };

      try {
        window.$PB.doAddOrder(onSuccess, onFailure);
      } catch (err) {
        setLoading(false);
        const errorMsg = err.message?.msg || err.message || 'Payment failed';
        setError(errorMsg);
        onErrorRef.current?.(errorMsg);
        reject(err);
      }
    });
  }, [isSDKLoaded]);

  const initializeWithToken = useCallback((newToken, sandbox = false) => {
    return new Promise((resolve, reject) => {
      initializedRef.current = false;

      const tryInit = () => {
        if (!isSDKLoaded()) return false;
        try {
          window.$PB.setMerchant(
            sandbox ? { pbtoken: newToken, homolog: true } : { pbtoken: newToken }
          );
          initializedRef.current = true;
          setReady(true);
          resolve();
          return true;
        } catch (err) {
          reject(err);
          return false;
        }
      };

      if (tryInit()) return;
      debugger
      let attempts = 0;
      const maxAttempts = 10;
      const checkInterval = setInterval(() => {
        attempts++;
        if (tryInit() || attempts >= maxAttempts) {
          clearInterval(checkInterval);
          if (attempts >= maxAttempts && !initializedRef.current) {
            reject(new Error('PagBrasil SDK failed to load'));
          }
        }
      }, 100);
    });
  }, [isSDKLoaded]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Reset hook state (for new payment attempt)
   */
  const reset = useCallback(() => {
    setError(null);
    setCardToken(null);
    setLoading(false);
  }, []);

  return {
    // State
    ready,
    loading,
    error,
    cardToken,

    // Methods
    setCardData,
    setCustomerData,
    setPaymentConfig,
    setOrderData,
    tokenize,
    processPayment,
    clearError,
    reset,
    isSDKLoaded,
    initializeWithToken
  };
};

export default usePagbrasilCard;
